/**
 * 智慧无限 FUNC_TYPE(0x60) 功能键 → 虚拟键盘展示码与文案
 * 与 CX87-3M keyboard_key_define.h 自定义功能码对齐
 */

export const ZHWX_FUNC_TYPE = 0x60;

/** 多媒体 / 系统消费者键（keyboard_key_define.h CON_TYPE） */
export const ZHWX_CON_TYPE = 0x20;

/** 与华奋达 synthetic 区间错开 */
export const ZHWX_FN_SYNTHETIC_BASE = 0x0f000000;

/** 出厂 Fn 键帽展示（与 KEYCODE_MAP 61697 一致） */
export const ZHWX_FN_UI_KEYCODE = 61697;

/**
 * UI 多媒体键码 → [CODE_L, CODE_H]（与 KEYCODE_MAP hex16 0x20xx / 0x21xx 一致）
 * 矩阵单元为 CON_TYPE(0x20) + CODE_L + CODE_H
 */
export const ZHWX_UI_TO_CONSUMER_CELL: Readonly<
  Record<number, readonly [number, number]>
> = {
  8303: [0x6f, 0x00], // 系统亮度+
  8304: [0x70, 0x00], // 系统亮度-
  8373: [0xb5, 0x00], // 下一曲
  8374: [0xb6, 0x00], // 上一曲
  8375: [0xb7, 0x00], // 停止
  8397: [0xcd, 0x00], // 播放/暂停
  8418: [0xe2, 0x00], // 静音
  8425: [0xe9, 0x00], // 音量+
  8426: [0xea, 0x00], // 音量-
  8579: [0x83, 0x01], // 音乐
  8594: [0x92, 0x01], // 计算器
  8596: [0x94, 0x01], // 我的电脑
  8739: [0x23, 0x02], // 浏览器首页
};

const ZHWX_CONSUMER_CELL_TO_UI = new Map<string, number>(
  Object.entries(ZHWX_UI_TO_CONSUMER_CELL).map(([ui, [codeL, codeH]]) => [
    `${codeH}-${codeL}`,
    Number(ui),
  ])
);

/**
 * 协议 CON_TYPE 仅 CODE_L（codeH=0）时的兜底映射
 * 兼容旧版误写成 KB_TYPE 的回读展示
 */
export const ZHWX_CONSUMER_CODE_TO_UI: Readonly<Record<number, number>> = {
  0x6f: 8303,
  0x70: 8304,
  0xb5: 8373,
  0xb6: 8374,
  0xb7: 8375,
  0xcd: 8397,
  0xe2: 8418,
  0xe9: 8425,
  0xea: 8426,
  0x83: 8579,
};

/** CON_TYPE 矩阵单元 → UI 多媒体码 */
export function resolveZhwxConsumerUiKeyCodeFromCell(
  codeL: number,
  codeH = 0
): number {
  const ui = ZHWX_CONSUMER_CELL_TO_UI.get(`${codeH & 0xff}-${codeL & 0xff}`);
  if (ui != null) return ui;
  if (!codeH) return resolveZhwxConsumerUiKeyCode(codeL);
  if (!codeL) return resolveZhwxConsumerUiKeyCode(codeH);
  // 固件偶发误标 CON_TYPE，codeH 仍为标准 HID 键码
  return resolveZhwxConsumerUiKeyCode(codeH);
}

/** 消费者键低字节 → UI 多媒体码（已解析后的 181 等也走此映射） */
export function resolveZhwxConsumerUiKeyCode(keyCode: number): number {
  if (!keyCode) return 0;
  return ZHWX_CONSUMER_CODE_TO_UI[keyCode & 0xff] ?? keyCode;
}

/** 功能码 → 中文名（改键页 Fn 层键帽文案） */
export const ZHWX_FUNC_NAME_MAP: Readonly<Record<number, string>> = {
  0x01: "Fn",
  0x02: "宏",
  0x03: "电脑快捷键",
  0x04: "锁 Win",
  0x05: "恢复出厂",
  0x06: "锁键",
  0x07: "WASD 改方向",
  0x20: "背光亮度设置",
  0x21: "灯光亮度+",
  0x22: "灯光亮度-",
  0x23: "背光速度设置",
  0x24: "灯光速度+",
  0x25: "灯光速度-",
  0x26: "背光方向设置",
  0x27: "灯光方向左",
  0x28: "灯光方向右",
  0x29: "色板设置",
  0x2a: "七色循环",
  0x2b: "关闭灯光",
  0x2c: "灯效切换",
  0x2d: "灯效 1",
  0x2e: "灯效 2",
  0x2f: "灯效 3",
  0x30: "灯效 4",
  0x31: "灯效 5",
  0x32: "灯效 6",
  0x33: "自定义灯光",
  0x34: "自定义 1",
  0x35: "自定义 2",
  0x36: "自定义 3",
  0x37: "自定义 4",
  0x38: "自定义 5",
  0x39: "自定义 6",
  0x3a: "LOGO 灯效",
  0x3b: "LOGO 七色",
  0x3c: "LOGO 亮度+",
  0x3d: "LOGO 亮度-",
  0x3e: "LOGO 速度+",
  0x3f: "LOGO 速度-",
  0x40: "侧灯灯效",
  0x41: "侧灯七色",
  0x42: "侧灯亮度+",
  0x43: "侧灯亮度-",
  0x44: "侧灯速度+",
  0x45: "侧灯速度-",
  0x46: "侧灯亮度循环",
  0x50: "宏集合",
  0x51: "宏 G1",
  0x52: "宏 G2",
  0x53: "宏 G3",
  0x54: "宏 G4",
  0x55: "宏 G5",
  0x56: "宏 G6",
  0x60: "切换蓝牙",
  0x61: "切换 2.4G",
  0x62: "配对",
  0x63: "蓝牙 1",
  0x64: "蓝牙 2",
  0x65: "蓝牙 3",
  0x66: "切换 2.4G",
  0x67: "切换 USB",
  0x68: "查询电量",
  0x69: "Win 模式",
  0x6a: "Mac 模式",
  0x6c: "用户层",
  0x6d: "办公层",
  0x6e: "游戏层",
  0x6f: "一键白光",
};

/** 与项目内 KEYCODE_MAP 图标码对齐，便于改键面板复用图标 */
const ZHWX_FUNC_ICON_KEYCODE: Readonly<Record<number, number>> = {
  0x01: ZHWX_FN_UI_KEYCODE,
  0x04: 61961,
  0x05: 61952,
  0x21: 62210,
  0x22: 62211,
  0x24: 62212,
  0x25: 62213,
  0x29: 62209,
  0x2b: 62215,
  0x2c: 62208,
  0x6f: 62215,
};

const zhwxFnNameBySynthetic = new Map<number, string>();

export function registerZhwxFnDisplayName(
  funcCode: number,
  name: string
): void {
  if (!funcCode || !name) return;
  zhwxFnNameBySynthetic.set(ZHWX_FN_SYNTHETIC_BASE | funcCode, name);
}

for (const [code, name] of Object.entries(ZHWX_FUNC_NAME_MAP)) {
  registerZhwxFnDisplayName(Number(code), name);
}

export function isZhwxFnSyntheticKeyCode(keyCode: number): boolean {
  return (
    Number.isFinite(keyCode) &&
    keyCode >= ZHWX_FN_SYNTHETIC_BASE &&
    keyCode < ZHWX_FN_SYNTHETIC_BASE + 0x01000000
  );
}

/** 功能键 / 图标码 / 合成码，用于 Fn 层合并时识别「已映射 Fn 功能」 */
export function isZhwxFnDisplayKeyCode(keyCode: number): boolean {
  if (!keyCode) return false;
  if (keyCode === ZHWX_FN_UI_KEYCODE) return true;
  if (isZhwxFnSyntheticKeyCode(keyCode)) return true;
  return Object.values(ZHWX_FUNC_ICON_KEYCODE).includes(keyCode);
}

export function lookupZhwxFuncName(funcCode: number): string | undefined {
  return ZHWX_FUNC_NAME_MAP[funcCode];
}

export function resolveZhwxFnFuncDisplayKeyCode(
  codeL: number,
  codeH = 0
): number {
  if (codeL === 0x01 && !codeH) return ZHWX_FN_UI_KEYCODE;
  const icon = ZHWX_FUNC_ICON_KEYCODE[codeL];
  if (icon != null) return icon;
  if (!codeL && !codeH) return 0;
  return ZHWX_FN_SYNTHETIC_BASE | codeL;
}

export type ZhwxFnKeyCodeInfo = {
  name: string;
  type: string;
  comm: string;
  hex16: string;
  typeof: string;
};

export function resolveZhwxFnSyntheticKeyInfo(
  keyCode: number
): ZhwxFnKeyCodeInfo | null {
  if (keyCode === ZHWX_FN_UI_KEYCODE) {
    return {
      name: "Fn",
      type: "特殊",
      comm: "",
      hex16: "",
      typeof: "special",
    };
  }
  if (isZhwxFnSyntheticKeyCode(keyCode)) {
    const name = zhwxFnNameBySynthetic.get(keyCode);
    if (!name) return null;
    return {
      name,
      type: "控制",
      comm: "",
      hex16: "",
      typeof: "control",
    };
  }
  return null;
}
